<template>
  <b-modal
    v-model="visible"
    size="xl"
    title="Envío de números de serie a la APP Copec"
    hide-footer
    centered
    scrollable
  >
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div v-else>
      <b-alert
        v-for="(error, i) in errors"
        :key="`e-${i}`"
        show
        variant="danger"
        class="mb-2"
        >{{ error }}</b-alert
      >
      <b-alert
        v-for="(aviso, i) in warnings"
        :key="`w-${i}`"
        show
        variant="warning"
        class="mb-2"
        >{{ aviso }}</b-alert
      >
      <b-alert v-if="notice" show variant="secondary" class="mb-2">{{
        notice
      }}</b-alert>

      <b-row class="mb-3">
        <b-col cols="12" md="4">
          <small class="text-black-60 d-block">Estado</small>
          <b-badge :variant="meta.variant || 'secondary'">{{
            statusLabel || "Sin envíos"
          }}</b-badge>
        </b-col>
        <b-col cols="12" md="4">
          <small class="text-black-60 d-block">Intentos realizados</small>
          <span>{{ attempts }}</span>
        </b-col>
        <b-col cols="12" md="4">
          <small class="text-black-60 d-block">Último envío correcto</small>
          <span>{{ lastSentAt ? formatoFecha(lastSentAt) : "—" }}</span>
        </b-col>
      </b-row>

      <div v-if="preview" class="mb-4">
        <h5 class="mb-2">Se enviará esto</h5>
        <b-row class="mb-2">
          <b-col cols="12" md="4">
            <small class="text-black-60 d-block">Código de la APP Copec</small>
            <strong>{{ preview.location_code }}</strong>
          </b-col>
          <b-col cols="12" md="4">
            <small class="text-black-60 d-block">Marca del inversor</small>
            <strong>{{ preview.brand }}</strong>
          </b-col>
          <b-col cols="12" md="4">
            <small class="text-black-60 d-block">Números de serie</small>
            <strong>{{ preview.serials.join(", ") || "—" }}</strong>
          </b-col>
        </b-row>
        <pre v-if="preview.body" class="bloque-json">{{ preview.url }}
{{ JSON.stringify(preview.body, null, 2) }}</pre>
      </div>

      <h5 class="mb-2">Historial de intentos</h5>
      <p v-if="!history.length" class="text-black-60">
        Todavía no se ha intentado ningún envío para esta plantilla.
      </p>

      <b-table
        v-else
        :items="history"
        :fields="camposHistorial"
        small
        responsive
        bordered
        class="mb-0"
      >
        <template #cell(created_at)="{ item }">
          {{ formatoFecha(item.created_at) }}
        </template>
        <template #cell(trigger_source)="{ item }">
          {{ origenes[item.trigger_source] || item.trigger_source }}
        </template>
        <template #cell(resultado)="{ item }">
          <b-badge :variant="item.ok ? 'primary' : 'danger'">
            {{ item.ok ? "Correcto" : "Error" }}
          </b-badge>
          <span v-if="item.stage === 'validation'" class="ml-1 text-black-60"
            >no se llegó a llamar</span
          >
        </template>
        <template #cell(detalle)="{ item }">
          <b-button
            size="sm"
            variant="outline-info-3"
            @click="alternar(item.id)"
            >{{ abiertos.includes(item.id) ? "Ocultar" : "Ver" }}</b-button
          >
          <b-collapse :visible="abiertos.includes(item.id)" class="mt-2">
            <pre class="bloque-json">{{ detalleDe(item) }}</pre>
          </b-collapse>
        </template>
      </b-table>
    </div>

    <div class="d-flex justify-content-end mt-4">
      <b-button variant="outline-info-3" class="mr-2" @click="visible = false"
        >Cerrar</b-button
      >
      <b-button
        v-if="puedeEnviar && !marcaExcluida"
        variant="primary"
        :disabled="enviando || !!errors.length"
        @click="$emit('enviar', placeChecklistId)"
      >
        <b-spinner v-if="enviando" small type="grow" class="mr-1"></b-spinner>
        {{ textoBoton }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from "vuex";
import {
  COPEC_ORIGENES,
  COPEC_SYNC_STATUS,
  copecMetaFromStatus,
} from "@/constants/integrations";

export default {
  name: "ModalCopecSyncHistory",
  props: {
    puedeEnviar: { type: Boolean, default: true },
  },
  data() {
    return {
      visible: false,
      loading: false,
      enviando: false,
      placeChecklistId: null,
      status: null,
      statusLabel: "",
      attempts: 0,
      lastSentAt: null,
      preview: null,
      errors: [],
      warnings: [],
      notice: "",
      history: [],
      abiertos: [],
      origenes: COPEC_ORIGENES,
      // Lo decide el backend, que es quien recorta la respuesta. Aquí no se
      // vuelve a mirar el rol: se pinta lo que llega.
      technical: false,
      campos: [
        { key: "created_at", label: "Fecha" },
        { key: "attempt", label: "Intento", class: "text-center" },
        { key: "trigger_source", label: "Origen" },
        { key: "triggered_by_email", label: "Usuario" },
        { key: "resultado", label: "Resultado", class: "text-center" },
      ],
      camposConDetalle: [
        { key: "created_at", label: "Fecha" },
        { key: "attempt", label: "Intento", class: "text-center" },
        { key: "trigger_source", label: "Origen" },
        { key: "triggered_by_email", label: "Usuario" },
        { key: "response_status", label: "Código", class: "text-center" },
        { key: "duration_ms", label: "ms", class: "text-center" },
        { key: "resultado", label: "Resultado", class: "text-center" },
        { key: "detalle", label: "Detalle" },
      ],
    };
  },
  computed: {
    marcaExcluida() {
      return this.status === "marca_excluida";
    },
    camposHistorial() {
      return this.technical ? this.camposConDetalle : this.campos;
    },
    meta() {
      return this.status ? copecMetaFromStatus(this.status) : COPEC_SYNC_STATUS.pendiente;
    },
    textoBoton() {
      if (this.status === "error") return "Reintentar envío";
      if (this.status === "enviado") return "Reenviar de todos modos";
      return "Enviar a la APP Copec";
    },
  },
  methods: {
    ...mapActions({ getCopecStatus: "integrations/getCopecStatus" }),
    async openModal(placeChecklistId) {
      this.placeChecklistId = placeChecklistId;
      this.visible = true;
      await this.recargar();
    },
    async recargar() {
      // Quien envía desde el menú de la fila llama a recargar sin haber abierto
      // nunca el modal; sin esto se pediría el estado de una plantilla nula.
      if (!this.placeChecklistId) return;
      this.loading = true;
      this.abiertos = [];
      const respuesta = await this.getCopecStatus({
        place_checklist_id: this.placeChecklistId,
        history: true,
      });
      this.loading = false;
      if (!respuesta || !respuesta.status) return;
      this.technical = !!respuesta.technical;
      this.status = respuesta.status;
      this.statusLabel = respuesta.status_label;
      this.attempts = respuesta.attempts || 0;
      this.lastSentAt = respuesta.last_sent_at;
      this.preview = respuesta.preview || null;
      this.errors = respuesta.errors || [];
      this.warnings = respuesta.warnings || [];
      this.notice = respuesta.notice || "";
      this.history = respuesta.history || [];
    },
    alternar(id) {
      this.abiertos = this.abiertos.includes(id)
        ? this.abiertos.filter((x) => x !== id)
        : this.abiertos.concat([id]);
    },
    detalleDe(item) {
      return [
        `${item.request_method} ${item.request_url}`,
        "",
        "Cabeceras:",
        JSON.stringify(item.request_headers, null, 2),
        "",
        "Cuerpo enviado:",
        JSON.stringify(item.request_body, null, 2),
        "",
        "Respuesta:",
        item.response_body || "(sin cuerpo)",
        item.error_message ? `\nError: ${item.error_message}` : "",
      ].join("\n");
    },
    formatoFecha(valor) {
      const fecha = new Date(valor);
      return fecha.toLocaleString("es-CL");
    },
  },
};
</script>

<style scoped>
.bloque-json {
  background: rgba(0, 0, 0, 0.04);
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
